import { defineStore } from 'pinia'

import { useBodyOverflowControl } from '~/composables/use-body-overflow-control'
import cities from '~/constants/cities'
import type { City, CitiesData } from '~/types/city'

type State = {
  citiesData: CitiesData
  filter: {
    query: string | null
  }
  popupStatus: boolean
  selectedCity: {
    id: string
    name: string
  }
}

const initialState = (): State => ({
  citiesData: cities,
  filter: { query: null },
  popupStatus: false,
  selectedCity: { id: '7700000000000', name: 'Москва' },
})

export const useCityStore = defineStore('city', {
  state: () => initialState(),
  getters: {
    alphabetCities: (state: State) => {
      const filtered = state.citiesData.all.filter(city =>
        !state.citiesData.popular.some(p => p.id && city.id && p.id === city.id)
      )

      const sorted = [...filtered].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      )

      const isCyrillicChar = (char: string) => /[а-яё]/i.test(char)

      return [
        sorted.filter(city => {
          const firstChar = city.name[0]?.toLowerCase()

          return isCyrillicChar(firstChar) && firstChar <= 'к';
        }),
        sorted.filter(city => {
          const firstChar = city.name[0]?.toLowerCase()

          return isCyrillicChar(firstChar) && firstChar > 'к' && firstChar <= 'с'
        }),
        sorted.filter(city => {
          const firstChar = city.name[0]?.toLowerCase()

          return isCyrillicChar(firstChar) && firstChar > 'с'
        })
      ]
    },
    filteredCities: (state: State) => {
      const all = [...state.citiesData.popular, ...state.citiesData.all] as City[]
      const q = state.filter.query?.trim().toLowerCase() || ''

      if (!q) {
        return all
      }

      return all.filter(city => {
        const cityName = city.name?.toLowerCase() || ''

        return cityName.includes(q)
      });
    }
  },
  actions: {
    closeCityPopup (): void {
      const bodyOverflowControl = useBodyOverflowControl()

      this.popupStatus = false
      bodyOverflowControl.enable()
    },
    getCityById (id: string): City | undefined {
      return [...this.citiesData.popular, ...this.citiesData.all].find(c => c.id === id)
    },
    openCityPopup (): void {
      const bodyOverflowControl = useBodyOverflowControl()

      this.popupStatus = true
      bodyOverflowControl.disable()
    },
    setCity (city: City) {
      this.selectedCity = city
    },
  },
  persist: {
    storage: typeof window !== 'undefined' ? localStorage : undefined,
    paths: ['selectedCity']
  }
})
